import React, { useEffect, useState } from 'react';
import { styled } from 'linaria/react';
import { cx } from 'linaria';

export const VideoWrapper = styled('div')`
  position: relative;
  padding-bottom: ${(props) =>
    props.fillHeight
      ? '0'
      : props.isPortrait
      ? '0'
      : `${props.bottompadding}%`};
  margin-bottom: 0px;
  width: ${(props) =>
    props.fillHeight ? '100%' : props.isPortrait ? 'auto' : '100%'} !important;
  height: ${(props) =>
    props.fillHeight ? '100%' : props.isPortrait ? '100%' : 'auto'} !important;
  max-height: 100%;
  max-width: 100%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  iframe {
    position: ${(props) =>
      props.fillHeight
        ? 'absolute'
        : props.isPortrait
        ? 'relative'
        : 'absolute'};
    top: ${(props) =>
      props.fillHeight ? '50%' : props.isPortrait ? 'auto' : '0'};
    left: ${(props) =>
      props.fillHeight ? '50%' : props.isPortrait ? 'auto' : '0'};
    transform: ${(props) =>
      props.fillHeight ? 'translate(-50%, -50%)' : 'none'};
    width: ${(props) => {
      if (props.fillHeight) {
        // Fit by width for landscape videos, by height for portrait videos
        // This ensures the entire video is visible
        return props.isPortrait ? 'auto' : '100%';
      }
      return props.isPortrait ? 'auto' : '100%';
    }};
    height: ${(props) => {
      if (props.fillHeight) {
        // Fit by height for portrait videos, by width for landscape videos
        return props.isPortrait ? '100%' : 'auto';
      }
      return props.isPortrait ? '100%' : '100%';
    }};
    max-width: 100%;
    max-height: 100%;
    display: block;
    border: none;
    aspect-ratio: ${(props) => {
      if (props.fillHeight && props.videoAspectRatio) {
        // Maintain video's aspect ratio to show entire video
        return props.videoAspectRatio;
      }
      return props.cssAspectRatio || 'unset';
    }};
  }

  &.youtube {
    background-color: white;
  }
`;

const Video = ({
  videoSrc,
  aspect = '16:9',
  setVideoThumbnail = () => {},
  fillHeight = false,
}) => {
  const [fetchedVideoData, setFetchedVideoData] = useState(false);
  const videoRatioToPercent = (aspect) => {
    const ratioParts = aspect?.split(':');

    const width = parseInt(ratioParts?.[0]);
    const height = parseInt(ratioParts?.[1]);

    if (isNaN(width) || isNaN(height)) {
      return 56.25;
    }

    return (height / width) * 100;
  };
  // Parse aspect ratio once
  const ratioParts = aspect?.split(':');
  const width = parseInt(ratioParts?.[0]);
  const height = parseInt(ratioParts?.[1]);

  // Check if video is portrait (height > width, like 9:16)
  const isPortrait = !isNaN(width) && !isNaN(height) && height > width;

  const videoBottomPadding = videoRatioToPercent(aspect);

  // Convert aspect ratio format from "9:16" to "9/16" for CSS
  const cssAspectRatio =
    isPortrait && !isNaN(width) && !isNaN(height)
      ? `${width}/${height}`
      : undefined;

  // For fillHeight mode, always use the video's aspect ratio
  const videoAspectRatio =
    !isNaN(width) && !isNaN(height) ? `${width}/${height}` : undefined;

  // Parse out the video ID from YouTube links (supports youtu.be, youtube.com/watch?v=, and youtube.com/shorts/)
  let youtubeVideoID = null;
  if (typeof videoSrc === 'string') {
    if (videoSrc.includes('youtu.be/')) {
      // Handles https://youtu.be/VIDEO_ID[?params]
      const match = videoSrc.match(/youtu\.be\/([^?&/]+)/);
      youtubeVideoID = match ? match[1] : null;
    } else if (videoSrc.includes('youtube.com/watch')) {
      // Handles https://www.youtube.com/watch?v=VIDEO_ID[&...]
      const match = videoSrc.match(/[?&]v=([^&]+)/);
      youtubeVideoID = match ? match[1] : null;
    } else if (videoSrc.includes('youtube.com/shorts/')) {
      // Handles https://www.youtube.com/shorts/VIDEO_ID[?params]
      const match = videoSrc.match(/youtube\.com\/shorts\/([^?&/]+)/);
      youtubeVideoID = match ? match[1] : null;
    }
  }

  useEffect(() => {
    if (!fetchedVideoData) {
      fetchVideoData();
    }
  }, []);

  // Fetches video data to set bottompadding for correct ratio
  const fetchVideoData = () => {
    return fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${youtubeVideoID}&format=json`
    )
      .then((response) => response.json())
      .then((data) => {
        setVideoThumbnail(data.thumbnail_url);
        setFetchedVideoData(true);
      })
      .catch((error) => console.error(error));
  };

  if (!videoSrc) {
    return null;
  }

  let url = videoSrc;
  let isYouTube = false;
  //check if vimeo
  if (videoSrc.includes('vimeo.com')) {
    const vPlayerUrl = videoSrc.replace(
      'vimeo.com/',
      'player.vimeo.com/video/'
    );
    url = vPlayerUrl + '?background=1';
    //check if youtube short URL (youtu.be)
  } else if (videoSrc.includes('youtu.be')) {
    isYouTube = true;
    // Extract video ID from youtu.be/VIDEO_ID format
    // Handles: https://youtu.be/VIDEO_ID, http://youtu.be/VIDEO_ID, youtu.be/VIDEO_ID
    const parts = videoSrc.split('youtu.be/');
    if (parts.length > 1) {
      const videoId = parts[1].split('?')[0].split('&')[0].split('/')[0];
      if (videoId) {
        // Minimal YouTube embed: no ads, no related videos, loop, muted autoplay, reduced branding
        url = `https://www.youtube.com/embed/${videoId}?autoplay=0&mute=1&loop=1&playlist=${videoId}&controls=1&rel=0&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1`;
      } else {
        return null;
      }
    } else {
      return null;
    }
    //check if youtube watch URL (youtube.com/watch)
  } else if (videoSrc.includes('youtube.com/watch')) {
    isYouTube = true;
    // Extract video ID from youtube.com/watch?v=VIDEO_ID format
    // Handles URLs with additional query parameters like &list=...&index=...
    const match = videoSrc.match(/[?&]v=([^&]+)/);
    const videoId = match ? match[1] : null;
    if (videoId) {
      // Minimal YouTube embed: no ads, no related videos, loop, muted autoplay, reduced branding
      url = `https://www.youtube.com/embed/${videoId}?autoplay=0&mute=1&loop=1&playlist=${videoId}&controls=1&rel=0&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1`;
    } else {
      return null;
    }
    //check if youtube shorts URL (youtube.com/shorts/)
  } else if (videoSrc.includes('youtube.com/shorts/')) {
    isYouTube = true;
    // Extract video ID from youtube.com/shorts/VIDEO_ID format
    // Handles: https://www.youtube.com/shorts/VIDEO_ID, http://youtube.com/shorts/VIDEO_ID
    const match = videoSrc.match(/youtube\.com\/shorts\/([^?&/]+)/);
    const videoId = match ? match[1] : null;
    if (videoId) {
      // Minimal YouTube embed: no ads, no related videos, loop, muted autoplay, reduced branding
      url = `https://www.youtube.com/embed/${videoId}?autoplay=0&mute=1&loop=1&playlist=${videoId}&controls=1&rel=0&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1`;
    } else {
      return null;
    }
  } else {
    return null;
  }

  const className = isYouTube ? 'video youtube' : 'video vimeo';

  return (
    <VideoWrapper
      className={cx(className)}
      bottompadding={videoBottomPadding}
      isPortrait={isPortrait}
      cssAspectRatio={cssAspectRatio}
      fillHeight={fillHeight}
      videoAspectRatio={videoAspectRatio}
    >
      <iframe
        title={`Video: ${url}`}
        src={url}
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen={false}
      />
    </VideoWrapper>
  );
};

export default Video;
